import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import * as XLSX from 'xlsx';
import { HttpClient } from '@angular/common/http';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ExcelFileService } from '../../../services/excel-file.service';
import { ToastService } from '../../../services/UI/toast.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-import-excel',
  templateUrl: './import-excel.component.html',
  styleUrl: './import-excel.component.scss'
})
export class ImportExcelComponent implements OnInit, OnChanges {
  headers: string[] = [];
  sampleHeaders: string[] = [];
  sampleData: any[] = [];
  importedData: any[] = [];
  uploadProgress: number = -1;
  @Input('type') type: string = 'employee';
  mandatoryColumns: string[] = [];
  selectedFile: File | null = null;
  formatMismatch: boolean = false;
  modalRef: NgbModalRef | null = null;

  // Define mandatory columns for different import types
  mandatoryColumnMap: { [key: string]: string[] } = {
    employee: ['full_name', 'email', 'phone', 'department', 'position', 'gender', 'joining_date', 'dob', 'position_criticity', 'available_mobility', 'married', 'role'],
    role: ['name'],
    skill: ['name', 'type'],
    department: ['name'],
    jobPosition: ['name', 'skills', 'certification', 'seniority']
  };

  constructor(
    private fileUploadService: ExcelFileService,
    private modal: NgbModal,
    private http: HttpClient,
    private toastService: ToastService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.updateMandatoryColumns();
    this.loadSampleData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['type']) {
      this.updateMandatoryColumns();
      this.loadSampleData();
    }
  }

  updateMandatoryColumns(): void {
    this.mandatoryColumns = this.mandatoryColumnMap[this.type] || [];
  }

  loadSampleData(): void {
    const defaultFilePath = `assets/import-formats/${this.type}.xlsx`;
    this.http.get(defaultFilePath, { responseType: 'arraybuffer' }).subscribe((data) => {
      const workbook = XLSX.read(data, { type: 'array' });
      if (!workbook.SheetNames.length) {
        console.error("Invalid Excel template!");
        return;
      }

      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      if (jsonData.length > 0) {
        this.sampleHeaders = jsonData[0] as string[];
        this.sampleData = jsonData.slice(1, 3); // Show only two rows
      }
    });
  }

  onFileChange(event: any, content: any): void {
    const target = event.target as HTMLInputElement;
    this.selectedFile = target.files?.[0] || null; // Store file but do not process
    
    if (this.selectedFile) {
      this.handleFile(this.selectedFile, content);
    }
  }

  handleFile(fileData: File, content: any): void {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });

      if (!workbook.SheetNames.length) {
        console.error("Invalid Excel file!");
        return;
      }

      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      if (jsonData.length > 0) {
        this.headers = jsonData[0] as string[];
        this.importedData = jsonData.slice(1);

        // Check if the headers match the sample file
        this.formatMismatch = !this.isValidFormat();
        if (this.formatMismatch) {
          this.showFormatErrorPopup(content);
          this.clearData();
        }
      }
    };

    reader.readAsArrayBuffer(fileData);
  }

  isValidFormat(): boolean {
    return JSON.stringify(this.headers) === JSON.stringify(this.sampleHeaders);
  }

  showFormatErrorPopup(content: any): void {
    this.modalRef = this.modal.open(content, { centered: true });
  }

  processFile(): void {
    if (!this.selectedFile || this.formatMismatch) {
      return;
    }

    const file = this.selectedFile;
    this.uploadProgress = 0;
    this.fileUploadService.uploadFile(file, this.type).subscribe((progress: number) => {
      this.uploadProgress = progress;
      if (progress === 100) {
        this.toastService.show(
          this.translate.instant("The file has been uploaded successfully and is being processed!"),
          {
            classname: "bg-success text-light",
            delay: 3000,
            icon: "fas fa-check-circle",
          }
        );
      }
    });
  }

  clearData(): void {
    this.importedData = [];
    this.selectedFile = null;
    this.uploadProgress = -1;
  }

  onCancel(): void {
    if (this.modalRef) {
      this.modalRef.close();
      this.modalRef = null; // Reset after closing
    } else {
      this.modal.dismissAll();
    }
  }  
  onSave(): void {
    this.processFile();
  }

  downloadSample(): void {
    const link = document.createElement("a");
    link.href = `assets/import-formats/${this.type}.xlsx`;
    link.download = `${this.type}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
